import { Component, ElementRef, ViewChild } from '@angular/core';
import { BlockCheckbox, CheckboxItem } from '@src/app/@shared/models/step.model';
import { AbstractControl, FormBuilder, FormControl } from '@angular/forms';
import { BlockAbstractComponent } from '@app/components/blocks/block.abstract.component';

export enum FormControlName {
  items = 'items',
}

interface CheckboxForm {
  [FormControlName.items]: FormControl<CheckboxItem[]>;
}

@Component({
  selector: 'pl-block-checkbox',
  templateUrl: './block-checkbox.component.html',
})
export class BlockCheckboxComponent extends BlockAbstractComponent<BlockCheckbox, CheckboxForm> {
  protected FormControlName: typeof FormControlName = FormControlName;

  @ViewChild('plCheckbox', { read: ElementRef }) public plCheckboxElement!: ElementRef;

  protected override initForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.nonNullable.group<CheckboxForm>({
      items: fb.nonNullable.control([]),
    });
  }

  protected override patchFormWithEmptyValue(): void {
    if (this.form) {
      this.form.patchValue(
        {
          [FormControlName.items]: [],
        },
        { emitEvent: false },
      );
    } else {
      throw new Error(`patchFormWithEmptyValue() this.form should not be null when this function is called`);
    }
  }

  protected override patchFormFromBlock(block: BlockCheckbox, valueFromLocalStorage: string | null): void {
    if (this.form) {
      const items: CheckboxItem[] = block.data.items;

      let markFormAsDirtyAndEmitValue: boolean = false;
      const numberCheckedItems: number = this.countNumberOfSelectedItems(items);
      if (numberCheckedItems === 0) {
        const itemsSelectedSavedInLocalStorage: string[] = valueFromLocalStorage
          ? JSON.parse(valueFromLocalStorage)
          : [];
        items.forEach((item: CheckboxItem) => {
          if (itemsSelectedSavedInLocalStorage.includes(item.value)) {
            item.checked = true;
            markFormAsDirtyAndEmitValue = true;
          }
        });
      }

      this.form.patchValue(
        {
          items,
        },
        { emitEvent: markFormAsDirtyAndEmitValue },
      );
      if (markFormAsDirtyAndEmitValue) {
        this.form?.controls.items.markAsDirty();
      }
    } else {
      throw new Error(`patchFormFromBlock() this.form should not be null when this function is called`);
    }
  }

  protected override getUpdatedBlock(): BlockCheckbox {
    if (this.form && this.block) {
      const control: AbstractControl | null = this.form.get(FormControlName.items);
      if (control) {
        const value: string = this.extractValueFromItems(control.value);
        this.courseContentService.saveShortCodesInLocalStorage({ [this.block.program_data_code]: value });

        return {
          ...this.block,
          data: {
            ...this.block.data,
            items: control.value,
            value: this.extractValueFromItems(control.value),
          },
        };
      }
    }

    throw new Error(`getUpdatedBlock() this.form or control should not be null when this function is called`);
  }

  private extractValueFromItems(items: CheckboxItem[]): string {
    return JSON.stringify(items.filter((item: CheckboxItem) => item.checked).map((item: CheckboxItem) => item.value));
  }

  protected override onProgrammaticFocus(): void {
    // preventScroll: true because scroll is handled by directive plScrollToInvalidControlOnSubmit
    this.plCheckboxElement.nativeElement.focus({ preventScroll: true });
  }

  private countNumberOfSelectedItems(items: CheckboxItem[]): number {
    return items.reduce((accumulator: number, currentValue: CheckboxItem) => {
      return accumulator + (currentValue.checked ? 1 : 0);
    }, 0);
  }
}
